import { css, html, TemplateResult } from "lit";
import type { ConfiguratorModuleKind } from "../../core/configurator/ConfiguratorData.js";
import { customElement, state, property } from "lit/decorators.js";
import { BaseElement } from "../../core/ui-kit/BaseElement.js";
import i18next from "i18next";
import { logger } from "../../util/Logger.js";

const styles = css`
  .proforma-page {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: white;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    animation: slideInFromRight 0.2s ease-in-out;
  }

  .proforma-page.closing {
    animation: slideOutFromRight 0.25s ease-out;
  }

  /* Pop-up Stilleri */
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20000;
    animation: fadeIn 0.3s ease-in-out;
  }

  .popup-content {
    background: white;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    max-width: 500px;
    width: 90%;
    animation: popupSlideIn 0.3s ease-out;
  }

  .popup-header {
    text-align: center;
    margin-bottom: 24px;
  }

  .popup-title {
    font-size: 24px;
    font-weight: 700;
    color: #333;
    margin: 0 0 8px 0;
  }

  .popup-subtitle {
    font-size: 16px;
    color: #666;
    margin: 0;
  }

  .popup-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .input-label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  .project-name-input {
    padding: 16px;
    border: 2px solid #e1e5e9;
    border-radius: 12px;
    font-size: 16px;
    transition: all 0.2s ease;
    outline: none;
  }

  .project-name-input:focus {
    border-color: #325a82;
    box-shadow: 0 0 0 3px rgba(50, 90, 130, 0.1);
  }

  .popup-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  .popup-btn {
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    min-width: 100px;
  }

  .popup-btn.cancel {
    background: #f8f9fa;
    color: #666;
    border: 1px solid #e1e5e9;
  }

  .popup-btn.cancel:hover {
    background: #e9ecef;
  }

  .popup-btn.create {
    background: #325a82;
    color: white;
  }

  .popup-btn.create:hover {
    background: #2a4d70;
  }

  .popup-btn.create:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes popupSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes slideInFromRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes slideOutFromRight {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(100%);
    }
  }

  .proforma-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 60px;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .back-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .page-title {
    font-size: 35px;
    font-weight: 600;
    color: #333;
  }

  .sort-btn {
    background: white;
    border: 1px solid #325a82;
    border-radius: 25px;
    padding: 8px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #325a82;
  }

  .proforma-content {
    flex: 1;
    display: flex;
    overflow: hidden;
  }

  .products-section {
    flex: 1;
    padding: 24px;
    overflow-y: auto;
  }

  .products-header {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #ddd;
    margin-bottom: 16px;
    font-weight: 600;
    color: #000;
    font-size: 22px;
    font-family: "Montserrat", sans-serif;
  }

  .product-item {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 16px;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .product-details {
    display: flex;
    gap: 16px;
  }

  .product-image {
    width: 120px;
    height: 120px;
    border-radius: 8px;
    background: #f0f0f0;
    flex-shrink: 0;
  }

  .product-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }

  .product-info {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px 0px;
  }

  .product-info h3 {
    font-size: 19px;
    font-weight: 700;
    color: #000;
    margin: 0 0 4px 0;
  }

  .product-dimensions {
    font-size: 16px;
    color: #87afdb;
    margin: 0;
  }

  .product-options {
    font-size: 14px;
    color: #666;
    margin: 2px 0 0 0;
  }

  .product-price {
    font-size: 16px;
    color: #000;
    margin: 4px 0 0 0;
  }

  .quantity-control {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .qty-input {
    width: 40px;
    text-align: center;
    border-radius: 4px;
    padding: 4px;
    font-size: 19px;
    border: none;
    font-weight: 700;
    outline: none;
  }

  .item-total {
    font-weight: 600;
    color: #333;
    text-align: right;
  }

  .preview-section {
    width: 400px;
    background: #f1f1f1;
    border-left: 1px solid #eee;
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 24px;
    padding-top: 50px;
  }

  .preview-image {
    width: 100%;
    height: auto;
    background: white;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }

  .preview-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
  }

  .preview-info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  .preview-title {
    font-size: 22px;
    font-weight: 700;
  }

  .preview-price {
    margin-bottom: 16px;
    font-weight: 700;
    font-size: 44px;
  }

  .price-amount {
    font-size: 28px;
    font-weight: 700;
    color: #000;
  }

  .price-currency {
    font-size: 20px;
    font-weight: 500;
    color: #000;
  }

  .preview-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
  }

  .action-btn {
    padding: 14px 14px;
    border-radius: 27px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    text-align: center;
    width: 70%;
  }

  .action-btn.primary {
    background: #325a82;
    color: white;
  }

  .action-btn.secondary {
    background: white;
    color: #325a82;
    border: 1px solid #325a82;
  }

  @media (max-width: 768px) {
    .proforma-content {
      flex-direction: column;
    }

    .preview-section {
      width: 100%;
      border-left: none;
      border-top: 1px solid #eee;
      padding: 16px;
    }

    .preview-image {
      height: 300px;
    }

    .products-section {
      padding: 16px;
    }

    .product-image {
      width: 40px;
      height: 40px;
    }

    .product-info h3 {
      font-size: 12px;
    }
  }

  .summary {
    margin-top: 20px;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 5px 0;
    font-size: 14px;
  }

  .summary-label {
    text-align: left;
  }

  .summary-value {
    text-align: right;
  }

  .total {
    font-weight: 700;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #000;
  }
`;

export interface ProformaProductOption {
  optionName: string;
  optionValue: string;
}

export interface ProformaProduct {
  id: number;
  name: string;
  price: number;
  quantity: number;
  uuid: string;
  options: ProformaProductOption[];
  /** Sahne satırı modül kind; seçenek süzme + materyal tekrarını önlemek için */
  configuratorModuleKind?: ConfiguratorModuleKind;
  dimensions: {
    width: number;
    height: number;
    depth: number;
  };
  thumbnail: string;
  materialOptions: {
    groupId: number;
    materialId: number;
    groupName: string;
    materialName: string;
  };
}

@customElement("sugar-proforma")
export class SugarProforma extends BaseElement {
  static styles = [styles];
  protected useShadowRoot = false;

  @property({ type: Boolean })
  visible = false;

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("visible")) {
      if (!this.visible) {
        this._isClosing = false; // Reset closing state when hidden
      }
    }
  }

  @property({ type: String })
  productImageUrl = "";

  @property({ type: Array })
  products: ProformaProduct[] = [];

  @property({ type: Number })
  totalPrice: number = 0;

  @state()
  private _sortDirection: "asc" | "desc" | "none" = "none";

  @state()
  private _showProjectNamePopup = false;

  @state()
  private _projectName = i18next.t("proforma.projectNamePlaceholder");

  @state()
  private _isClosing = false;

  private _modelName = i18next.t("proforma.modelSummary");

  /** Modül satırında renk/grup zaten options’ta ise API material satırını tekrarlama. */
  private shouldShowProformaMaterialBlock(product: ProformaProduct): boolean {
    if (!product.materialOptions?.groupName?.trim()) return false;
    const k = product.configuratorModuleKind;
    if (k === "upholstery" || k === "armSet" || k === "accessory") {
      if (
        product.options?.some(
          (o) =>
            o.optionName === "Kumaş Tipi" || o.optionName === "Kumaş Rengi",
        )
      ) {
        return false;
      }
    }
    if (k === "leg") {
      if (
        product.options?.some(
          (o) => o.optionName === "Ayak Rengi" && o.optionValue?.trim(),
        )
      ) {
        return false;
      }
    }
    return true;
  }

  handleClose() {
    // Hemen animasyonu başlat - DOM'a direkt müdahale
    const proformaPage = this.querySelector(".proforma-page") as HTMLElement;
    if (proformaPage) {
      proformaPage.classList.add("closing");
    }

    this._isClosing = true;
    this.requestUpdate();

    // Animasyon bitince parent'a haber ver
    setTimeout(() => {
      this._isClosing = false; // Reset state
      this.dispatchEvent(new CustomEvent("close-proforma"));
    }, 250); // 0.25s animasyon süresi
  }

  handleSort() {
    this._sortDirection =
      this._sortDirection === "none"
        ? "asc"
        : this._sortDirection === "asc"
          ? "desc"
          : "none";
    this.requestUpdate();
  }

  getSortedProducts(): ProformaProduct[] {
    if (!this.products) return [];
    const products = [...this.products];

    if (this._sortDirection === "none") return products;

    return products.sort((a, b) => {
      return this._sortDirection === "asc"
        ? Number(a.price) - Number(b.price)
        : Number(b.price) - Number(a.price);
    });
  }

  handleShare() {
    this._projectName = this._modelName || "";
    this._showProjectNamePopup = true;
    this.requestUpdate();
  }

  handleProjectNameCancel() {
    this._showProjectNamePopup = false;
    this._projectName = "";
    this.requestUpdate();
  }

  handleProjectNameCreate() {
    if (!this._projectName.toString().trim()) {
      return;
    }

    this._showProjectNamePopup = false;
    this.requestUpdate();

    this.loadPDFLibrary()
      .then(() => this.generatePDF())
      .catch((error) => {
        logger.error("PDF oluşturma hatası:", error);
        alert(i18next.t("proforma.pdfError"));
      });
  }

  handleProjectNameInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this._projectName = target.value;
    this.requestUpdate();
  }

  private loadPDFLibrary(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).html2pdf) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error(i18next.t("proforma.pdfLibraryError")));

      document.head.appendChild(script);
    });
  }

  private async generatePDF() {
    try {
      const products = this.getSortedProducts();
      const totalPrice = this.totalPrice;

      const container = document.createElement("div");
      container.innerHTML = this.generatePDFContent(products, totalPrice);
      document.body.appendChild(container);
      await new Promise((resolve) => setTimeout(resolve, 200));

      container.style.padding = "20px";

      const loadingEl = document.createElement("div");
      loadingEl.style.cssText =
        "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(255,255,255,0.9);padding:20px;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);z-index:10001;";
      loadingEl.innerHTML = i18next.t("proforma.pdfGenerating");
      document.body.appendChild(loadingEl);

      const opt = {
        margin: 1,
        filename: `${
          this._projectName.toString().trim() || "tasarim-ozeti"
        }-${new Date().toLocaleDateString(i18next.t("__locale__") + "")}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 3,
          useCORS: true,
          logging: false,
          letterRendering: true,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
        pagebreak: { mode: ["css", "legacy"] },
      };

      await (window as any).html2pdf().set(opt).from(container).save();
      document.body.removeChild(container);
      document.body.removeChild(loadingEl);
    } catch (error) {
      logger.error("PDF oluşturma hatası:", error);
      alert(i18next.t("proforma.pdfError2"));
    }
  }

  private generatePDFContent(
    products: ProformaProduct[],
    totalPrice: number,
  ): string {
    const today = new Date().toLocaleDateString(i18next.t("__locale__") + "");
    logger.info(i18next.t("__locale__"));

    return `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Inter', sans-serif;
          color: #333;
          line-height: 1.5;
          padding: 40px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 40px;
        }

        .brand {
          display: flex;
          justify-content: space-between;
          width: 100%;
          align-items: flex-start;
        }

        .brand-name {
          font-size: 24px;
          font-weight: 700;
          color: #333;
        }

        .reference {
          font-size: 14px;
          color: #666;
          margin-top: 5px;
        }

        .logo {
          width: 100px;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          object-fit: contain;
          overflow: hidden;
        }

        .logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .products-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }

        .table-header {
          border-bottom: 1px solid #000;
          border-top: 1px solid #000;
          font-weight: 500;
        }

        .table-header th {
          padding: 10px;
          text-align: left;
          font-size: 14px;
        }

        .avoid-break {
          page-break-inside: avoid;
          break-inside: avoid;
        }


        .product-row {
          border-bottom: 1px solid #ddd;
        }

        .product-row td {
          padding: 20px 10px;
          vertical-align: top;
        }

        .product-image {
          width: 100px;
          height: 100px;
          object-fit: contain;
        }

        .product-details {
          margin-left: 10px;
        }

        .product-name {
          font-weight: 500;
          margin-bottom: 5px;
        }

        .product-specs {
          font-size: 14px;
          color: #666;
          margin: 2px 0;
        }

        .price {
          text-align: right;
          font-weight: 500;
        }

        .summary {
          margin-top: 20px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 5px 0;
          font-size: 14px;
        }

        .summary-label {
          text-align: left;
        }

        .summary-value {
          text-align: right;
        }

        .total {
          font-weight: 700;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #000;
        }

        .footer {
          margin-top: 40px;
          font-size: 12px;
          color: #666;
        }

        .footer ul {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .footer li {
          margin: 5px 0;
          padding-left: 15px;
          position: relative;
        }

        .footer li:before {
          content: "•";
          position: absolute;
          left: 0;
        }

        .preview-section {
          margin-top: 40px;
          page-break-inside: avoid;
          break-inside: avoid;
        }

        .preview-title {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 20px;
        }

        .preview-image {
          width: 100%;
          max-width: 800px;
          object-fit: contain;
          max-height: 600px;
        }
      </style>
      
      <div class="header">
        <div class="brand">
          <div>
            <div class="brand-name">Normod</div>
            <div class="reference">${i18next.t("proforma.offerNo")} : ${
              this._projectName
            }</div>
          </div>
          <div class="logo">
            <img src="https://normod.com/cdn/shop/files/normod_logo_large_1500x750_copy_1_140x@2x.png?v=1625151735" />
          </div>
        </div>
      </div>

      <table class="products-table">
        <tr class="table-header">
          <th style="width: 10%">${i18next.t("proforma.quantity")}</th>
          <th style="width: 60%">${i18next.t("proforma.product")}</th>
          <th style="width: 15%">${i18next.t("proforma.price")}</th>
          <th style="width: 15%">${i18next.t("proforma.subtotal")}</th>
        </tr>
        ${products
          .map((product) => {
            const itemTotal = product.price * product.quantity;

            return `
            <tr class="product-row avoid-break">
              <td>${product.quantity}</td>
              <td>
                <div style="display: flex;">
                  <img src="${
                    product.thumbnail
                  }" class="product-image" alt="${i18next.t(
                    product.name,
                  )}" crossorigin="anonymous"/>
                  <div class="product-details">
                    <div class="product-name">${i18next.t(product.name)}</div>
                    <div class="product-specs">${i18next.t(
                      "proforma.dimensions",
                    )} : ${product.dimensions.width}x${
                      product.dimensions.depth
                    }${product.dimensions.height ? "x" + product.dimensions.height : ""} cm</div>
                    ${
                      product.options && product.options.length > 0
                        ? product.options
                            .filter((o) => o?.optionValue?.trim())
                            .sort((a, b) =>
                              a?.optionName?.localeCompare(
                                b?.optionName,
                                "tr",
                                {
                                  sensitivity: "base",
                                },
                              ),
                            )
                            .map(
                              (option) =>
                                `<div class="product-specs">${option?.optionName} : ${option?.optionValue}</div>`,
                            )
                            .join("")
                        : ""
                    }
                    ${
                      this.shouldShowProformaMaterialBlock(product)
                        ? `
                      <div class="product-specs">${i18next.t(
                        "proforma.materialType",
                      )} : ${i18next.t(product.materialOptions!.groupName)}</div>
                      <div class="product-specs">${i18next.t(
                        "proforma.materialColor",
                      )} : ${i18next.t(
                        product.materialOptions!.materialName,
                      )}</div>
                    `
                        : ""
                    }
                  </div>
                </div>
              </td>
              <td class="price">₺${product.price.toLocaleString(
                i18next.t("__locale__") + "",
              )},00</td>
              <td class="price">₺${itemTotal.toLocaleString(
                i18next.t("__locale__") + "",
              )},00</td>
            </tr>
          `;
          })
          .join("")}
      </table>

      <div class="summary avoid-break">
        <div class="summary-row">
          <span class="summary-label">${i18next.t("proforma.orderDate")}</span>
          <span class="summary-value">${today}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">${i18next.t("proforma.subtotal")}</span>
          <span class="summary-value">₺${totalPrice.toLocaleString(
            i18next.t("__locale__") + "",
          )},00</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">${i18next.t("proforma.discount")}</span>
          <span class="summary-value">₺0,00</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">${i18next.t(
            "proforma.taxIncluded",
          )}</span>
          <span class="summary-value">₺${(totalPrice * 0.1).toLocaleString(
            i18next.t("__locale__") + "",
          )},00</span>
        </div>
        <div class="summary-row total">
          <span class="summary-label">${i18next.t("proforma.total")}</span>
          <span class="summary-value">₺${totalPrice.toLocaleString(
            "tr-TR",
          )},00</span>
        </div>
      </div>

      <div class="footer avoid-break">
        <ul>
          <li>${i18next.t("proforma.footer1")}</li>
          <li>${i18next.t("proforma.footer2")}</li>
          <li>${i18next.t("proforma.footer3")}</li>
          <li>${i18next.t("proforma.footer4", {
            date: new Date().toLocaleDateString(i18next.t("__locale__") + ""),
          })}</li>
          <li>${i18next.t("proforma.footer5")}</li>
          <li>${i18next.t("proforma.footer6")}</li>
          <li>${i18next.t("proforma.footer7")}</li>
        </ul>
      </div>

      ${
        this.productImageUrl
          ? `
        <div class="preview-section avoid-break">
          <div class="preview-title">${i18next.t("proforma.preview3d")}</div>
          <img src="${
            this.productImageUrl
          }" class="preview-image" alt="${i18next.t("proforma.preview3d")}"/>
        </div>
      `
          : ""
      }
    `;
  }

  private getFormattedPrice(price: number): string {
    return price.toLocaleString("tr-TR");
  }

  protected renderHtml(): TemplateResult {
    // Kapanma animasyonu sırasında da render et
    if (!this.visible && !this._isClosing) return html``;

    return html`
      ${this._showProjectNamePopup
        ? html`
            <div class="popup-overlay">
              <div class="popup-content">
                <div class="popup-header">
                  <h2 class="popup-title">
                    ${i18next.t("proforma.setProjectName")}
                  </h2>
                  <p class="popup-subtitle">
                    ${i18next.t("proforma.pdfWillBeSavedAs")}
                  </p>
                </div>

                <div class="popup-form">
                  <div class="input-group">
                    <label class="input-label"
                      >${i18next.t("proforma.projectName")}</label
                    >
                    <input
                      type="text"
                      class="project-name-input"
                      .value=${this._projectName}
                      @input=${this.handleProjectNameInput}
                      @keydown=${(e: KeyboardEvent) => {
                        if (
                          e.key === "Enter" &&
                          this._projectName.toString().trim()
                        ) {
                          this.handleProjectNameCreate();
                        } else if (e.key === "Escape") {
                          this.handleProjectNameCancel();
                        }
                      }}
                      placeholder="${i18next.t(
                        "proforma.projectNamePlaceholder",
                      )}"
                      maxlength="50"
                      autocomplete="off"
                    />
                  </div>

                  <div class="popup-actions">
                    <button
                      class="popup-btn cancel"
                      @click=${this.handleProjectNameCancel}
                    >
                      ${i18next.t("proforma.cancel")}
                    </button>
                    <button
                      class="popup-btn create"
                      @click=${this.handleProjectNameCreate}
                      ?disabled=${!this._projectName.toString().trim()}
                    >
                      ${i18next.t("proforma.createPdf")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          `
        : ""}

      <div class="proforma-page ${this._isClosing ? "closing" : ""}">
        <div class="proforma-content">
          <div class="products-section">
            <div class="proforma-header">
              <div class="header-left">
                <button class="back-btn" @click=${this.handleClose}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 12H5"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 19L5 12L12 5"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <h1 class="page-title">
                  ${i18next.t("proforma.modelSummary")}
                </h1>
              </div>
              <button class="sort-btn" @click=${this.handleSort}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="#325A82">
                  <use href="#sort-icon"></use>
                </svg>
                <span>
                  ${this._sortDirection === "asc"
                    ? i18next.t("proforma.priceAsc")
                    : this._sortDirection === "desc"
                      ? i18next.t("proforma.priceDesc")
                      : i18next.t("proforma.sort")}
                </span>
              </button>
            </div>

            <div class="products-header">
              <div>${i18next.t("proforma.productDetails")}</div>
              <div>${i18next.t("proforma.quantity")}</div>
              <div>${i18next.t("proforma.price")}</div>
            </div>

            ${this.getSortedProducts().map((product) => {
              const itemTotal = product.price * product.quantity;

              return html`
                <div class="product-item">
                  <div class="product-details">
                    <div class="product-image">
                      <img
                        src="${product.thumbnail}"
                        alt="${i18next.t(product.name)}"
                      />
                    </div>
                    <div class="product-info">
                      <h3>${i18next.t(product.name)}</h3>
                      <p class="product-dimensions">
                        ${product.dimensions.width}${product.dimensions.depth
                          ? "x" + product.dimensions.depth
                          : ""}${product.dimensions.height
                          ? "x" + product.dimensions.height
                          : ""}
                        ${i18next.t("proforma.cm")}
                      </p>
                      ${product.options && product.options.length > 0
                        ? product.options
                            .filter((o) => o?.optionValue?.trim())
                            .sort((a, b) =>
                              a?.optionName?.localeCompare(
                                b?.optionName,
                                "tr",
                                {
                                  sensitivity: "base",
                                },
                              ),
                            )
                            .map(
                              (option) => html`
                                <p class="product-options">
                                  ${i18next.t(option?.optionName)}:
                                  ${i18next.t(option?.optionValue)}
                                </p>
                              `,
                            )
                        : ""}
                      ${this.shouldShowProformaMaterialBlock(product)
                        ? html`
                            <p class="material-info">
                              ${i18next.t(product.materialOptions!.groupName)} -
                              ${i18next.t(
                                product.materialOptions!.materialName,
                              )}
                            </p>
                          `
                        : ""}
                      <p class="product-price">
                        ${product.price.toLocaleString("tr-TR")},00
                        ${i18next.t("__currency__")}
                      </p>
                    </div>
                  </div>

                  <div class="quantity-control">
                    <span class="qty-input">${product.quantity}</span>
                  </div>

                  <div class="item-total">
                    ${itemTotal.toLocaleString("tr-TR")},00
                    ${i18next.t("__currency__")}
                  </div>
                </div>
              `;
            })}
          </div>

          <div class="preview-section">
            <div class="preview-image">
              <img
                src="${this.productImageUrl}"
                alt="${i18next.t("proforma.preview3d")}"
              />
            </div>

            <div class="preview-info">
              <div class="preview-title">${i18next.t(this._modelName)}</div>

              <div class="preview-price">
                <span class="price-amount"
                  >${this.getFormattedPrice(this.totalPrice)}</span
                >
                <span class="price-currency"
                  >,00 ${i18next.t("__currency__")}</span
                >
              </div>

              <div class="preview-actions">
                <button class="action-btn primary">
                  ${i18next.t("proforma.addToCart")}
                </button>
                <button class="action-btn secondary" @click=${this.handleShare}>
                  ${i18next.t("proforma.share")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
